/**
 * Modern Beast - Stamp Variants on Collection / Symmetry compatibility
 *
 * Stamp creates variant cards after Shopify Liquid has rendered the normal
 * Symmetry product card. Symmetry's hover manager keeps its own image stack,
 * so the cloned card can keep the parent product's active/hover image state.
 *
 * This script detects Stamp-style variant links (?variant=...), loads the
 * product JSON once per product, then aligns Symmetry's active and hover image
 * classes with the selected variant. It also falls back to the primary image
 * when there is no safe sequential hover image, preventing blank hover states.
 */
(function () {
  'use strict';

  var CARD_SELECTOR = '.product-block';
  var productCache = new Map();
  var pendingCards = new Set();
  var rafScheduled = false;

  function normalizeUrl(value) {
    if (!value) return '';

    if (typeof value === 'object') {
      value = value.src || value.url || (value.preview_image && (value.preview_image.src || value.preview_image.url)) || '';
    }

    if (!value || typeof value !== 'string') return '';

    try {
      var url = new URL(value, window.location.origin);
      return url.origin + url.pathname;
    } catch (e) {
      return value.split('?')[0];
    }
  }

  function imageSrc(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value.src) return value.src;
    if (value.url) return value.url;
    if (value.preview_image) return imageSrc(value.preview_image);
    return '';
  }

  function imageId(value) {
    if (!value || typeof value !== 'object') return null;
    return value.id || value.media_id || (value.preview_image && value.preview_image.id) || null;
  }

  function getVariantLink(card) {
    var links = card.querySelectorAll('a[href]');

    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      if (!href || href.indexOf('variant=') === -1) continue;

      try {
        var url = new URL(href, window.location.origin);
        var variantId = url.searchParams.get('variant');
        if (variantId && /\/products\//.test(url.pathname)) {
          return { element: links[i], url: url, variantId: String(variantId) };
        }
      } catch (e) {
        // Ignore malformed/non-URL hrefs.
      }
    }

    return null;
  }

  function getProductJsonUrl(productUrl) {
    var match = productUrl.pathname.match(/\/products\/([^/?#]+)/i);
    if (!match) return null;

    var handle = match[1].replace(/\.js$/i, '');
    var root = '/';

    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      root = window.Shopify.routes.root;
    }

    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'products/' + encodeURIComponent(decodeURIComponent(handle)) + '.js';
  }

  function loadProduct(productUrl) {
    var jsonUrl = getProductJsonUrl(productUrl);
    if (!jsonUrl) return Promise.resolve(null);

    if (!productCache.has(jsonUrl)) {
      productCache.set(jsonUrl, fetch(jsonUrl, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      }).then(function (response) {
        if (!response.ok) throw new Error('Product JSON request failed: ' + response.status);
        return response.json();
      }).catch(function (error) {
        console.warn('[MB Stamp/Symmetry] Could not load product data.', error);
        return null;
      }));
    }

    return productCache.get(jsonUrl);
  }

  function getVariantPrimary(variant, product) {
    var featured = variant && (variant.featured_media || variant.featured_image);
    var src = imageSrc(featured);
    var id = imageId(featured);

    if (!src && variant && variant.featured_image && typeof variant.featured_image === 'string') {
      src = variant.featured_image;
    }

    if (!src && product) {
      src = imageSrc(product.featured_media || product.featured_image);
      id = id || imageId(product.featured_media || product.featured_image);
    }

    return {
      id: id ? String(id) : null,
      src: src,
      normalized: normalizeUrl(src)
    };
  }

  function getProductImages(product) {
    var result = [];

    if (product && Array.isArray(product.media) && product.media.length) {
      product.media.forEach(function (media) {
        var type = media.media_type || media.type;
        if (type && type !== 'image') return;

        var src = imageSrc(media.preview_image || media);
        if (!src) return;

        result.push({
          id: media.id ? String(media.id) : null,
          src: src,
          normalized: normalizeUrl(src)
        });
      });
    }

    if (!result.length && product && Array.isArray(product.images)) {
      product.images.forEach(function (img) {
        var src = imageSrc(img);
        if (!src) return;
        result.push({
          id: imageId(img) ? String(imageId(img)) : null,
          src: src,
          normalized: normalizeUrl(src)
        });
      });
    }

    return result;
  }

  function findSequentialHover(product, variant, primary) {
    var images = getProductImages(product);
    if (!images.length || !primary || !primary.src) return null;

    var primaryIndex = -1;

    if (primary.id) {
      primaryIndex = images.findIndex(function (img) {
        return img.id && img.id === primary.id;
      });
    }

    if (primaryIndex < 0 && primary.normalized) {
      primaryIndex = images.findIndex(function (img) {
        return img.normalized === primary.normalized;
      });
    }

    if (primaryIndex < 0 || primaryIndex + 1 >= images.length) return null;

    var candidate = images[primaryIndex + 1];
    if (!candidate || !candidate.src) return null;

    // If the next image is the featured image of another variant, it is most
    // likely the next colourway rather than a hover image. In that case we keep
    // the current variant image on hover instead of showing the wrong colour.
    var candidateIsAnotherVariantPrimary = (product.variants || []).some(function (otherVariant) {
      if (!otherVariant || String(otherVariant.id) === String(variant.id)) return false;
      var otherPrimary = getVariantPrimary(otherVariant, product);
      return otherPrimary.normalized && otherPrimary.normalized === candidate.normalized && otherPrimary.normalized !== primary.normalized;
    });

    return candidateIsAnotherVariantPrimary ? null : candidate;
  }

  function nodeImageUrl(node) {
    if (!node) return '';

    var img = node.querySelector('img');
    if (img) {
      var imgUrl = img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '';
      if (imgUrl) return normalizeUrl(imgUrl);
    }

    var bg = node.querySelector('.rimage-background');
    if (bg) {
      var lazySrc = bg.getAttribute('data-lazy-bgset-src') || '';
      if (lazySrc) return normalizeUrl(lazySrc);

      var inlineBg = bg.style.backgroundImage || '';
      var match = inlineBg.match(/url\(["']?(.*?)["']?\)/i);
      if (match && match[1]) return normalizeUrl(match[1]);
    }

    return '';
  }

  function findImageNode(card, image) {
    if (!card || !image) return null;

    var nodes = Array.prototype.slice.call(card.querySelectorAll('.product-block__image'));

    if (image.id) {
      var byId = nodes.find(function (node) {
        return String(node.getAttribute('data-media-id') || '') === String(image.id);
      });
      if (byId) return byId;
    }

    if (image.normalized || image.src) {
      var wanted = image.normalized || normalizeUrl(image.src);
      var byUrl = nodes.find(function (node) {
        return nodeImageUrl(node) === wanted;
      });
      if (byUrl) return byUrl;
    }

    return null;
  }

  function setBackgroundImage(node, image) {
    if (!node || !image || !image.src) return;

    var bg = node.querySelector('.rimage-background');
    if (!bg) {
      bg = document.createElement('div');
      bg.className = 'lazyloaded rimage-background fade-in';
      bg.setAttribute('data-sizes', 'auto');
      bg.setAttribute('data-parent-fit', 'contain');
      node.appendChild(bg);
    }

    bg.style.backgroundImage = 'url("' + image.src.replace(/"/g, '%22') + '")';
    bg.setAttribute('data-lazy-bgset-src', image.src);
    bg.setAttribute('data-bgset', image.src);
    bg.classList.remove('lazyload--manual', 'lazyload');
    bg.classList.add('lazyloaded');

    if (image.id) node.setAttribute('data-media-id', image.id);
  }

  function setPrimaryImage(node, image) {
    if (!node || !image || !image.src) return;

    var img = node.querySelector('img');
    if (!img || node.classList.contains('product-block__image--secondary')) {
      setBackgroundImage(node, image);
      return;
    }

    img.setAttribute('src', image.src);
    img.setAttribute('srcset', image.src);
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');

    if (image.id) node.setAttribute('data-media-id', image.id);

    var wrapper = img.closest('.rimage-wrapper');
    if (wrapper) wrapper.removeAttribute('data-bg-set');
  }

  function createSecondaryNode(referenceNode, image) {
    var node = document.createElement('div');
    node.className = 'product-block__image product-block__image--secondary rimage-wrapper lazyload--placeholder';
    if (image && image.id) node.setAttribute('data-media-id', image.id);

    var bg = document.createElement('div');
    bg.className = 'lazyloaded rimage-background fade-in';
    bg.setAttribute('data-sizes', 'auto');
    bg.setAttribute('data-parent-fit', 'contain');
    node.appendChild(bg);

    setBackgroundImage(node, image);

    if (referenceNode && referenceNode.parentNode) {
      referenceNode.parentNode.appendChild(node);
    }

    return node;
  }

  function applyVariantImages(card, product, variant) {
    if (!card || !product || !variant) return;

    var imageContainer = card.querySelector('.image-cont');
    if (!imageContainer) return;

    var primary = getVariantPrimary(variant, product);
    if (!primary.src) return;

    var hover = findSequentialHover(product, variant, primary);
    var allImages = Array.prototype.slice.call(card.querySelectorAll('.product-block__image'));
    if (!allImages.length) return;

    var primaryNode = findImageNode(card, primary);
    if (!primaryNode) {
      primaryNode = card.querySelector('.product-block__image--primary') || allImages[0];
      setPrimaryImage(primaryNode, primary);
    } else if (primaryNode.classList.contains('product-block__image--secondary')) {
      setBackgroundImage(primaryNode, primary);
    } else {
      setPrimaryImage(primaryNode, primary);
    }

    var hoverNode = hover ? findImageNode(card, hover) : null;

    // This Symmetry copy contains a custom `alt=hover_image` override which can
    // make a node's data-media-id correct while its actual background points at
    // another image. Always synchronize the selected hover node's source too.
    if (hoverNode && hoverNode !== primaryNode) {
      setBackgroundImage(hoverNode, hover);
    }

    if (hover && (!hoverNode || hoverNode === primaryNode)) {
      hoverNode = allImages.find(function (node) {
        return node !== primaryNode && node.classList.contains('product-block__image--secondary');
      });

      if (!hoverNode) {
        hoverNode = createSecondaryNode(primaryNode, hover);
        allImages.push(hoverNode);
      } else {
        setBackgroundImage(hoverNode, hover);
      }
    }

    allImages.forEach(function (node) {
      node.classList.remove('product-block__image--active', 'product-block__image--show-on-hover');
    });

    primaryNode.classList.remove('product-block__image--inactivated');
    primaryNode.classList.add('product-block__image--active');

    if (hoverNode && hoverNode !== primaryNode) {
      hoverNode.classList.remove('product-block__image--inactivated');
      hoverNode.classList.add('product-block__image--show-on-hover');
    } else {
      // No valid second image for this variant. Keep the primary image visible
      // on hover instead of allowing Symmetry to fade to an empty/wrong image.
      primaryNode.classList.add('product-block__image--show-on-hover');
    }

    imageContainer.classList.add('image-cont--with-secondary-image');

    var dots = imageContainer.querySelectorAll('.product-block__image-dot');
    if (dots.length) {
      Array.prototype.forEach.call(dots, function (dot) {
        dot.classList.remove('product-block__image-dot--active');
      });

      var activeIndex = Array.prototype.indexOf.call(card.querySelectorAll('.product-block__image'), primaryNode);
      if (activeIndex >= 0 && dots[Math.min(activeIndex, dots.length - 1)]) {
        dots[Math.min(activeIndex, dots.length - 1)].classList.add('product-block__image-dot--active');
      }
    }

    card.setAttribute('data-mb-stamp-variant-fixed', String(variant.id));
  }

  function repairNativeSezeriumHover(card) {
    if (!card || !card.isConnected) return;

    // Stamp/Sezerium also touches products that are not being split into
    // multiple visible variants. Those cards keep Symmetry's native markup,
    // but because they are cloned/rewritten after Symmetry's initial
    // ProductBlockManager pass, the native hover image can remain
    // `product-block__image--inactivated` (display:none !important).
    // In that state the markup contains a perfectly valid secondary image,
    // but hovering can never reveal it.
    var isSezeriumCard = card.classList.contains('sezerium-product') ||
      card.hasAttribute('data-sezerium-product') ||
      card.hasAttribute('data-sezerium-variant');

    if (!isSezeriumCard) return;

    // A card with ?variant=... is a real split-variant card and is handled by
    // applyVariantImages() below. This branch is only for the original/native
    // product card (for example a product with just one variant).
    if (getVariantLink(card)) return;

    var imageContainer = card.querySelector('.image-cont--with-secondary-image');
    if (!imageContainer) return;

    var hoverNode = card.querySelector('.product-block__image--show-on-hover');

    // Be defensive in case Stamp preserved the image stack but dropped the
    // show-on-hover class: use the image immediately after the active image.
    if (!hoverNode) {
      var activeNode = card.querySelector('.product-block__image--active');
      if (activeNode) {
        var next = activeNode.nextElementSibling;
        while (next && !next.classList.contains('product-block__image')) {
          next = next.nextElementSibling;
        }
        if (next) {
          hoverNode = next;
          hoverNode.classList.add('product-block__image--show-on-hover');
        }
      }
    }

    if (!hoverNode) return;

    // Symmetry normally converts lazyload--manual and removes the inactivated
    // class during its first ProductBlockManager.loadImages() run. Since Stamp
    // can create/replace the card afterwards, reproduce only the small part we
    // need here rather than rebinding all of Symmetry's product-card events.
    var bg = hoverNode.querySelector('.rimage-background');
    if (bg) {
      var src = bg.getAttribute('data-lazy-bgset-src') ||
        bg.getAttribute('data-bgset') || '';

      if (!src) {
        var inlineBg = bg.style.backgroundImage || '';
        var match = inlineBg.match(/url\([\"']?(.*?)[\"']?\)/i);
        if (match && match[1]) src = match[1];
      }

      if (src) {
        // data-lazy-bgset-src in this theme is a single master image URL, so
        // assigning it directly is safe and avoids waiting for a lazySizes
        // lifecycle that already happened before Stamp rewrote the card.
        bg.style.backgroundImage = 'url("' + src.replace(/"/g, '%22') + '")';
        bg.setAttribute('data-bgset', src);
        bg.classList.remove('lazyload--manual', 'lazyload');
        bg.classList.add('lazyloaded');
      }
    }

    hoverNode.classList.remove('product-block__image--inactivated');
    card.setAttribute('data-mb-stamp-native-hover-fixed', 'true');
  }

  function repairCard(card) {
    if (!card || !card.isConnected) return;

    repairNativeSezeriumHover(card);

    var variantLink = getVariantLink(card);
    if (!variantLink) return;

    loadProduct(variantLink.url).then(function (product) {
      if (!product || !card.isConnected) return;

      var variant = (product.variants || []).find(function (item) {
        return String(item.id) === variantLink.variantId;
      });

      if (!variant) return;
      applyVariantImages(card, product, variant);
    });
  }

  function flushPendingCards() {
    rafScheduled = false;
    var cards = Array.from(pendingCards);
    pendingCards.clear();
    cards.forEach(repairCard);
  }

  function queueCard(card) {
    if (!card || !card.matches || !card.matches(CARD_SELECTOR)) return;
    pendingCards.add(card);

    if (!rafScheduled) {
      rafScheduled = true;
      window.requestAnimationFrame(flushPendingCards);
    }
  }

  function scan(root) {
    root = root || document;

    if (root.matches && root.matches(CARD_SELECTOR)) queueCard(root);

    if (root.querySelectorAll) {
      root.querySelectorAll(CARD_SELECTOR).forEach(queueCard);
    }
  }

  function startObserver() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            scan(node);

            var closest = node.closest && node.closest(CARD_SELECTOR);
            if (closest) queueCard(closest);
          });
        } else if (mutation.type === 'attributes') {
          var card = mutation.target.closest && mutation.target.closest(CARD_SELECTOR);
          if (card) queueCard(card);
        }
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'data-product-id', 'data-variant-id', 'data-variant']
    });
  }

  function init() {
    scan(document);
    startObserver();

    // Stamp can finish cloning/re-writing cards after the theme's own deferred
    // scripts. These rescans cover that initialization window without polling.
    [250, 750, 1500, 3000].forEach(function (delay) {
      window.setTimeout(function () { scan(document); }, delay);
    });

    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target || document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();